import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@jumbo/components/JumboDialog/hooks/useDisclosure';
import DialogConfirm from '@jumbo/components/JumboDialog/components/DialogConfirm';

import Stack from '@mui/material/Stack';
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Card, CardContent } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { usePagination } from 'app/hooks/usePagination';
import { Table } from 'app/components';
import Div from '@jumbo/shared/Div';
import { deleteUser, getUserList } from 'app/redux/slice/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Loader from '@jumbo/components/Loader';
import ClearIcon from '@mui/icons-material/Clear';
import { userType } from 'app/helpers/userType';

const User = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const userData = useSelector((state) => state?.user);
  const [searchText, setSearchText] = useState(
    localStorage.getItem('user-search') ?? '',
  );
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: 'user' });
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem('permission')),
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      const rolePermission = permissionData?.filter((item) => {
        return item?.moduleName?.includes('User') && item;
      });
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === 'user-create') {
            permissionValue.createPermission = true;
          }
          if (num?.name === 'user-delete') {
            permissionValue.deletePermission = true;
          }
          if (num?.name === 'user-update') {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };
  useEffect(() => {
    getPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const columns = [
    { id: 'name', label: 'Name', sort: true },
    {
      id: 'role.name',
      label: 'Role',
      sort: true,
      render: (row) => row?.role?.name,
    },
    { id: 'email', label: 'Email', sort: true },
    { id: 'mobileNumber', label: 'Mobile Number', sort: true },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      minWidth: 100,
      render: (row) => (
        <>
          {permission.updatePermission && (
            <IconButton
              onClick={() => {
                navigate(`/edit-user/${row._id}`);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {permission.deletePermission && (
            <IconButton
              onClick={() => {
                deleteStageAction(row);
              }}
            >
              <DeleteIcon color={'error'} />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const DeleteUser = () => {
    dispatch(deleteUser(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: 'success',
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: 'error',
          });
        }
      });
  };

  const getData = () => {
    localStorage.setItem('user-search', searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
    };
    if (orderBy !== '') {
      value.sort = order === 'asc' ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getUserList(value));
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleChangePage(0);
      getData();
    }
  };
  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };
  return (
    <>
      {userData.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: 'flex', mb: 3 }}>
            <Div sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={10}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === '' ? 'none' : 'flex',
                            }}
                            onClick={() => setSearchText('')}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: 'column', sm: 'row' }}
                sx={{ mb: 3 }}
              >
                <Typography variant={'h2'} mb={0}>
                  Users List
                </Typography>
                {permission.createPermission && (
                  <LoadingButton
                    onClick={() => {
                      navigate('/add-user');
                    }}
                    type="submit"
                    variant="contained"
                  >
                    Add user
                  </LoadingButton>
                )}
              </Stack>
              <Table
                columns={columns}
                rows={userData.list}
                page={page}
                rowsPerPage={rowsPerPage}
                count={userData.total}
                Order={order}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editActionId={'/edit-user/'}
              />
            </CardContent>
          </Card>
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete this user?"
            onConfirm={DeleteUser}
            btnLabels={{ cancel: 'Cancel', confirm: 'Delete' }}
          />
        </>
      )}
    </>
  );
};

export default User;
